/**
 * Usage analytics — one module, so the vendor is a decision in one file rather than a script tag
 * pasted into four.
 *
 * ## THIS FILE IS THE KIT. Do not edit the copy in a product.
 *
 * It was pasted into two repositories and drifted by 22 lines. Those 22 lines were not cosmetic:
 * they were a US/EU region split that would have made portfolio numbers unmergeable forever, a
 * stale comment steering the ingest host at a CDN that had since been built, and a
 * `disable_session_recording` expression that left the recorder ON for the two consoles where
 * other people's data is on screen — while the privacy page said there was no session replay.
 *
 * Every consequential analytics finding in the 2026-08-12 audit was a consequence of that fork.
 * So: edit here, run `make kit-sync` in each product, commit both. `.kit-manifest.json` records
 * a hash per vendored file and `make verify-standard` fails on an edit in place.
 *
 * WHY NOT GOOGLE ANALYTICS, since that is the default answer. This audience is developers, and
 * GA4 is blocked by uBlock Origin, Brave, Pi-hole and every DNS blocklist in common use. The
 * undercount on a developer product runs 30-50%, and it is not random: it skews toward exactly
 * the technically-sophisticated visitors whose behaviour matters most. GA4 also sets cookies, so
 * EU visitors need a consent banner, which suppresses the number again. The result is a figure
 * that is both wrong and pessimistic, which is worse than no figure.
 *
 * SO IT IS FIRST-PARTY. Events go to `/ingest` on this origin, which the CDN forwards to the
 * vendor. A blocker sees a same-origin request to the site you are already on. That is not a
 * trick to defeat consent — it is what makes the measurement honest, and everything below is
 * still opt-out and still cookieless on the public pages.
 *
 * ONE REGION, PORTFOLIO-WIDE. `ui_host` is `us.posthog.com` and it is not a per-product
 * decision. A PostHog organisation cannot span the US and EU clouds, so two products on two
 * regions can never be one query, and events cannot be moved between them afterwards. authoxi
 * was on US and agitome on EU; agitome moved, before either had data to lose.
 *
 * COOKIELESS ON PUBLIC PAGES, deliberately. `persistence: "memory"` means no cookie, no
 * localStorage and no cross-session identity, which keeps these pages outside the consent-banner
 * regime and keeps the promise on /privacy true. A console is a different bargain: it is behind
 * a key, the operator has an account, and understanding which screens they use is the job.
 *
 * WHAT IT CANNOT SEE, stated here so nobody builds a strategy on it: AI crawlers. GPTBot,
 * ClaudeBot and PerplexityBot fetch HTML and never execute this — and posthog-js drops them by
 * user-agent anyway. If the question is "is this content being read by assistants", the answer
 * lives in CDN access logs, not here.
 */

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

/** The project key. Unset makes every function here a no-op rather than an error.
 *
 *  THE NO-OP IS WHY THIS NEEDS A BUILD-TIME GUARD. Both products shipped `VITE_ANALYTICS_KEY:""`
 *  in every bundle from their first deploy and recorded nothing, because a silent no-op is
 *  indistinguishable from a working install until somebody goes looking for the data. The
 *  release scripts now refuse to build without it (rule ANL-1). */
const KEY = env.VITE_ANALYTICS_KEY ?? "";

/** Where events go. Same-origin by default; see the note above about blockers. */
const HOST = env.VITE_ANALYTICS_HOST ?? "/ingest";

/**
 * Which product this is, stamped on every event.
 *
 * ONE PROJECT, SPLIT BY THIS PROPERTY is the intended shape. Separate projects per product make
 * every portfolio question a manual union, and the split cannot be undone later — where this
 * property can be filtered on, grouped by, or ignored.
 */
const PRODUCT_NAME = env.VITE_ANALYTICS_PRODUCT ?? "aeternm";

type Props = Record<string, string | number | boolean | undefined>;

interface Vendor {
  init(opts: { key: string; host: string; cookieless: boolean }): void;
  page(path: string, props?: Props): void;
  event(name: string, props?: Props): void;
  identify(id: string, props?: Props): void;
  reset(): void;
}

/**
 * PostHog, loaded on demand.
 *
 * Imported dynamically so the ~50KB never enters the critical path of a marketing page whose
 * whole job is to render fast for a crawler. A page that got slower to measure how fast it is
 * would be an unusually silly trade.
 */
const posthog: Vendor = (() => {
  let ph: any = null;
  let loading: Promise<any> | null = null;

  async function load() {
    if (ph) return ph;
    if (!loading) {
      loading = import("posthog-js").then((m) => {
        ph = m.default ?? m;
        return ph;
      });
    }
    return loading;
  }

  return {
    init({ key, host, cookieless }) {
      void load().then((p) =>
        p.init(key, {
          api_host: host,
          // Without this, posthog-js rewrites asset URLs to the vendor's host and the blocker
          // problem comes straight back through the side door.
          ui_host: "https://us.posthog.com",
          persistence: cookieless ? "memory" : "localStorage+cookie",
          // We send our own, with the route we resolved — an SPA's automatic pageview fires
          // before the router has decided anything and attributes every view to the entry URL.
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: !cookieless,
          // OFF EVERYWHERE, including behind a key, and it is `true` rather than an expression.
          //
          // It read `disable_session_recording: cookieless` in one of the two forked copies,
          // which enabled the recorder on precisely the surfaces the privacy page promised would
          // not have one — the tenant console and the staff console, the two places other
          // people's data is on screen. A boolean cannot drift the way an expression can.
          disable_session_recording: true,
        }),
      );
    },
    page(path, props) {
      void load().then((p) =>
        p.capture("$pageview", { $current_url: path, product: PRODUCT_NAME, ...props }),
      );
    },
    event(name, props) {
      void load().then((p) => p.capture(name, { product: PRODUCT_NAME, ...props }));
    },
    identify(id, props) {
      void load().then((p) => p.identify(id, props));
    },
    reset() {
      void load().then((p) => p.reset());
    },
  };
})();

const vendor = posthog;

let started = false;

/**
 * Start collecting. Safe to call more than once, and a no-op without a key.
 *
 * `cookieless` is the public-page mode: no cookie, no persistent id, no session recording, no
 * autocapture. Pass `false` only on an authenticated surface.
 */
export function startAnalytics({ cookieless = true }: { cookieless?: boolean } = {}): void {
  // `typeof window` guards the prerender: this module is imported by pages that are rendered to
  // static HTML in Node, where touching a browser API throws and takes the whole build with it.
  if (started || !KEY || typeof window === "undefined") return;
  // Honour the browser's own signal rather than making people find a setting of ours.
  if (navigator.doNotTrack === "1" || (window as any).doNotTrack === "1") return;
  started = true;
  vendor.init({ key: KEY, host: HOST, cookieless });
}

/** One page view. Call it with the resolved route, not `location.pathname`. */
export function trackPage(path: string, props?: Props): void {
  if (!started) return;
  vendor.page(path, props);
}

/**
 * One thing somebody did.
 *
 * Names are `noun_verb` and past tense — `key_created`, `grade_run`, `approval_signed` — because
 * a funnel built from `Clicked Button 3` is a funnel nobody can read six weeks later. The
 * taxonomy itself lives in `events.ts`, as constants, so a typo is a compile error rather than a
 * silent hole in a funnel.
 */
export function trackEvent(name: string, props?: Props): void {
  if (!started) return;
  vendor.event(name, props);
}

/**
 * Attach the current tenant. Authenticated surfaces only — never call this on a public page.
 *
 * The TENANT, never the operator. A tenant id is an account already billed and supported; a
 * person's name or email in an analytics vendor is a different promise from the one /privacy
 * makes.
 */
export function identifyTenant(tenantId: string, props?: Props): void {
  if (!started) return;
  vendor.identify(tenantId, props);
}

/** Forget them. Call on sign-out, or the next operator on this browser inherits the last one. */
export function resetAnalytics(): void {
  if (!started) return;
  vendor.reset();
}
